import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClients, useCommissions, useUsines, useVentes } from "@/lib/donnees";
import { autorise, useNiveau } from "@/lib/session";
import { Kpi, Kpis, Section, Segment, Tag } from "@/ui/primitives";
import { Table, dansUnChamp } from "@/ui/Table";
import type { Colonne } from "@/ui/Table";
import { dateFr, euro, euroCourt } from "@/lib/format";
import { ETAPES, etapeDe } from "@/domain/ventes";
import type { EtapeVente, Vente } from "@/types/metier";

export function Ventes() {
  const navigue = useNavigate();
  const { donnees: ventes, chargement, erreur } = useVentes();
  const { donnees: usines } = useUsines();
  const { donnees: clients } = useClients();
  const { donnees: commissions } = useCommissions();
  const niveau = useNiveau();
  const voitUsine = autorise(niveau, "agent");
  const [etape, setEtape] = useState<EtapeVente | null>(null);

  const nomUsine = useMemo(
    () => new Map(usines.map((u) => [u.id, u.nom] as const)),
    [usines],
  );
  const nomClient = useMemo(
    () => new Map(clients.map((c) => [c.id, c.nom] as const)),
    [clients],
  );
  const commParVente = useMemo(
    () => new Map(commissions.map((c) => [c.vente_id, c] as const)),
    [commissions],
  );

  const parEtape = useMemo(() => {
    const m: Record<string, Vente[]> = {};
    for (const e of ETAPES) m[e.cle] = [];
    for (const v of ventes) m[v.etape]?.push(v);
    return m;
  }, [ventes]);

  const lignes = etape ? (parEtape[etape] ?? []) : ventes;
  const somme = (l: Vente[]) => l.reduce((s, v) => s + Number(v.montant), 0);

  const colonnes: (Colonne<Vente> | false)[] = [
    {
      cle: "reference",
      titre: "Document",
      type: "ref",
      filtre: "texte",
      sous: (v) =>
        `${v.client_id ? (nomClient.get(v.client_id) ?? "—") : "—"} · ${etapeDe(v.etape).court}`,
    },
    {
      cle: "client_id",
      titre: "Client",
      mobile: false,
      filtre: "select",
      texte: (v) => (v.client_id ? (nomClient.get(v.client_id) ?? "—") : "—"),
    },
    voitUsine && {
      cle: "usine_id",
      titre: "Usine",
      mobile: false,
      filtre: "select",
      texte: (v) => nomUsine.get(v.usine_id) ?? "—",
    },
    {
      cle: "montant",
      titre: "Montant HT",
      court: "Montant",
      type: "euro",
      total: true,
      nombre: (v) => Number(v.montant),
    },
    {
      cle: "etape",
      titre: "Étape",
      filtre: "select",
      texte: (v) => etapeDe(v.etape).nom,
      rendu: (v) => (
        <Tag ton={etapeDe(v.etape).ton} dot>
          {etapeDe(v.etape).court}
        </Tag>
      ),
    },
    {
      cle: "commission",
      titre: "Commission",
      type: "euro",
      total: true,
      mobile: false,
      nombre: (v) => {
        const c = commParVente.get(v.id);
        if (!c) return 0;
        return autorise(niveau, "agent") ? Number(c.montant) : Number(c.agent_montant);
      },
      rendu: (v) => {
        const c = commParVente.get(v.id);
        if (!c) return <span className="locked">aucune</span>;
        return euro(autorise(niveau, "agent") ? Number(c.montant) : Number(c.agent_montant));
      },
    },
    { cle: "date_document", titre: "Date", type: "date", mobile: false,
      rendu: (v) => <span className="mono">{dateFr(v.date_document)}</span> },
  ];

  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;

  return (
    <>
      <div className="filterbar">
        <Segment
          label="Étape"
          valeur={etape}
          onChange={setEtape}
          options={[
            { cle: null, nom: "Tous les documents" },
            ...ETAPES.map((e) => ({ cle: e.cle, nom: e.nom, n: parEtape[e.cle]?.length ?? 0 })),
          ]}
        />
      </div>

      <div className="pad">
        <Kpis>
          <Kpi
            k="Devis en cours"
            v={euroCourt(somme(parEtape.devis ?? []))}
            n={`${parEtape.devis?.length ?? 0} documents`}
          />
          <Kpi
            k="Commandes engagées"
            v={euroCourt(somme(parEtape.commande ?? []))}
            n={`${parEtape.commande?.length ?? 0} documents`}
          />
          <Kpi
            k="Encaissé"
            v={euroCourt(somme(parEtape.paye ?? []))}
            n={`${parEtape.paye?.length ?? 0} documents`}
          />
        </Kpis>

        <Section titre="Documents" note={chargement ? "chargement…" : `${lignes.length} documents`}>
          <Table
            lignes={lignes}
            cle={(v) => v.id}
            colonnes={colonnes}
            chercher="Document ou client"
            onClic={(v, ev) => {
              if (!dansUnChamp(ev)) navigue(`/ventes/${v.id}`);
            }}
          />
        </Section>
      </div>
    </>
  );
}
